import { Beruf, PartnerFamilienstand } from '@mng/domain';
import {
  DefaultValueHandler,
  DependencyFunction,
  findObjectForPropertyInModelContext,
  IModelContext,
  ISimpleValidationResult,
  IValidationResult,
  ModelDependency,
  ModelGroup,
  ModelItem,
  ModelOption,
  ValidationFunction,
  ValueOptions
} from '@mng/model';
import { familienStandValueOptions, geschlechtValueOptions, StammdatenBase } from '../../allgemein/stammdaten-base';
import { ITEM_ANGEBOTE_META_DATA } from '../../antrag.model.paths';
import { Antragdata, Person } from '../../antrag.model.types';
import { antragContext } from '../antrag-context';
import { IAntragFragen, IZusatzFlagsFuerSonderBehandlung } from '../logic/antrag-fragen';
import {
  findAntragdataInContext,
  findPersonInContext,
  hatPersonGvAngebotsBeziehung,
  hatPersonUvAngebotsBeziehung,
  isForNatuerlichePersonRequired
} from '../logic/person-model.logic';
import { getAlterForGeburtstag } from '@mng/mng-ts-helpers';

export function createStammdatenEditMetaDatenModel(
  fieldOptions: { modelId: string; valueOptions: ModelOption[] }[]
): ModelGroup {
  const modelGroup: ModelGroup = createStammdatenModel(false);
  fieldOptions.forEach((fieldOption) => {
    let model = <any>modelGroup.getItemById(fieldOption.modelId);
    if (!model) {
      modelGroup.groups.forEach((group) => {
        model = <any>group.getItemById(fieldOption.modelId);
        if (model) {
          return;
        }
      });
    }
    if (model) {
      model.options.withValueOptions(new ValueOptions(fieldOption.valueOptions));
    }
  });
  return modelGroup;
}
export const MESSAGE_ID_ABWEICHENDESOZVERSANSTALT = 'svAnstaltAbweichend';
export function createStammdatenModel(readOnly = true): ModelGroup {
  const stammdaten = new StammdatenBase();
  stammdaten.init(readOnly);

  // DependencyFunctions definieren
  stammdaten.titel.withFunctionDependency(() => false);
  stammdaten.titelNachgestellt.withFunctionDependency(() => false);
  stammdaten.berufstitel.withFunctionDependency(() => false);
  stammdaten.partnerNr.withRequired();
  stammdaten.vorname.withFunctionDependency(isForNatuerlichePersonRequired);
  stammdaten.name.withRequired();
  stammdaten.geschlecht
    .withFunctionDependency(isForNatuerlichePersonRequired, false)
    .withValueOptions(geschlechtValueOptions);
  stammdaten.geschlecht.withFunctionDependency(isForNatuerlichePersonRequired);
  stammdaten.gebdatum.withFunctionDependency(isForNatuerlichePersonRequired);
  stammdaten.familienstand
    .withValueOptions(familienStandValueOptions)
    .withFunctionDependency(isFamilienstandRequired)
    .withValidation(famStdValid);
  stammdaten.geburtsland.withFunctionDependency(isGeburtslandRequired);
  stammdaten.svAnstalt.withFunctionDependency(isSozVersDatenRequired).withValidation(svAnstaltValid);
  stammdaten.svNummer.withFunctionDependency(isSozVersDatenRequired, false);
  stammdaten.hausnr.withFunctionDependency(isAdresseRequired);
  stammdaten.ikz.withFunctionDependency(isAdresseRequired);
  stammdaten.ort.withFunctionDependency(isAdresseRequired);
  stammdaten.plz.withFunctionDependency(isAdresseRequired);
  stammdaten.strasse.withFunctionDependency(isAdresseRequired);
  stammdaten.nationalitaet.withFunctionDependency(isNationalitaetRequired);

  stammdaten.juristischePerson.withReadOnly();
  stammdaten.berufsbezeichnung
    .withFunctionDependency(isBerufsInfoRequired)
    .withValidation(berufsbezeichnungValid)
    .withDefaultValueHandler(berufsbezeichnungVorbelegen);
  stammdaten.beschaeftigungsart
    .withFunctionDependency(isBerufsInfoRequired)
    .withValidation(beschaeftigungsArtValid)
    .withDefaultValueHandler(beschaeftigungsArtVorbelegen);
  stammdaten.beschaeftigtBei
    .withFunctionDependency(isArbeitgeberVisible)
    .withValidation(isArbeitgeberValid)
    .withRequiredFunction(isArbeitgeberRequired);
  stammdaten.adresse.withFunctionDependency(isAdresseRequired);

  const stammdatenModelGroup: ModelGroup = stammdaten.createStammdatenModelGroup();
  stammdatenModelGroup.withFunctionDependency(isForPersonWithRolesRequired);
  return stammdatenModelGroup;
}

export const svAnstaltValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const person: Person = findPersonInContext(modelContext);
  const simpleValidationResult: ISimpleValidationResult = isSvAnstaltValid(modelContext.value, person);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: MESSAGE_ID_ABWEICHENDESOZVERSANSTALT,
      message: simpleValidationResult.message
    };
  }
};
export const famStdValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const person: Person = findPersonInContext(modelContext);
  const simpleValidationResult: ISimpleValidationResult = isFamStdValid(modelContext.value, person);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: 'familienstand',
      message: simpleValidationResult.message
    };
  }
};

export function isSvAnstaltValid(svAnstaltAuswahl: number, person: Person): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };
  const isGVAntrag: boolean = hatPersonGvAngebotsBeziehung(person);
  if (
    isGVAntrag &&
    !!person.abweichendeSozVersAnstalt &&
    person.abweichendeSozVersAnstalt.length > 0 &&
    !person.abweichendeSozVersAnstalt.some((svAnstalt) => svAnstalt === Number(person.stammdaten.sozversanstalt)) &&
    !person.abweichendeSozVersAnstalt.some((aSozVersA) => aSozVersA === svAnstaltAuswahl)
  ) {
    result.valid = false;
    result.message =
      'Die gewählte Sozialversicherung im Angebot stimmt nicht mit den gespeicherten Stammdaten überein.';
  }
  return result;
}

export function isFamStdValid(famstd: string, person: Person): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };
  const isGVAntrag: boolean = hatPersonGvAngebotsBeziehung(person);
  if (famstd === PartnerFamilienstand.UNBEKANNT) {
    result.valid = false;
    result.message = 'Familienstand darf nicht "unbekannt" sein';
  }
  if (!isGVAntrag && !famstd) {
    result.valid = false;
    result.message = 'Familienstand muss bekanntgegeben werden';
  }
  return result;
}

export function hasRoles(person: Person): boolean {
  const hasRolesResult =
    person.personRollen &&
    Object.keys(person.personRollen).length > 0 &&
    (!!person.personRollen.vn ||
      !!person.personRollen.vn2 ||
      !!person.personRollen.vp ||
      (person.personRollen.treugeber && person.personRollen.treugeber.length > 0) ||
      !!person.personRollen.vp2 ||
      !!person.personRollen.praemienzahler ||
      (person.personRollen.gesetzlicherVertreter && person.personRollen.gesetzlicherVertreter.length > 0));
  return person && person.personRollen && hasRolesResult;
}

export const isForPersonWithRolesRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findObjectForPropertyInModelContext(modelContext, ITEM_ANGEBOTE_META_DATA);
  const person: Person = findPersonInContext(modelContext);

  return !!person && sindStammdatenFuerPersonNotwendig(antrag, person);
};

export function sindStammdatenFuerPersonNotwendig(antrag: Antragdata, person: Person): boolean {
  return hasRoles(person);
}

export const isFamilienstandRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  return isFamilienstandForPersonRequired(antrag, person);
};

export function isFamilienstandForPersonRequired(antrag: Antragdata, person: Person): boolean {
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);
  const lvZuatzFragenSonderbehandlung: IZusatzFlagsFuerSonderBehandlung =
    antragContext.getZusatzFragenSonderbehandlung(person);
  return fragen.familienstand && !lvZuatzFragenSonderbehandlung.only_paragraph_3_1_15_tarifeExistsInAntrag;
}

export const isGeburtslandRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  return isGeburtslandForPersonRequired(antrag, person);
};

export function isGeburtslandForPersonRequired(antrag: Antragdata, person: Person): boolean {
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);
  const lvZuatzFragenSonderbehandlung: IZusatzFlagsFuerSonderBehandlung =
    antragContext.getZusatzFragenSonderbehandlung(person);
  return fragen.geburtsland && !lvZuatzFragenSonderbehandlung.only_paragraph_3_1_15_tarifeExistsInAntrag;
}

export const isBerufsInfoRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  const alter = getAlterForGeburtstag(person.stammdaten.gebdatum);
  return isBerufsInfoForPersonRequired(antrag, person) && alter >= 6;
};

export const isArbeitgeberVisible: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const person: Person = findPersonInContext(modelContext);
  const isVN: boolean = person.personRollen.vn;
  const isVP: boolean = person.personRollen.vp;
  const isGVAntrag: boolean = hatPersonGvAngebotsBeziehung(person);
  const isUVAntrag: boolean = hatPersonUvAngebotsBeziehung(person);
  const alter: number = getAlterForGeburtstag(person.stammdaten.gebdatum);
  return ((isGVAntrag && isVN) || (isUVAntrag && isVP)) && !person.juristischePerson && alter >= 14;
};

export const BESCHAEFTIGUNGSARTEN_OHNE_ARBEITGEBER = [
  2, // selbständig
  5, // arbeitslos
  6, // Schüler
  8, // Student
  9 // Hausfrau
];

export function isArbeitgeberRequired(modelContext: IModelContext): boolean {
  // Wenn der Arbeitgeber sichtbar wird, dann ist er auch required, ausgenommen, es wurde eine Beschäftigungsart gewählt, die keinen Arbeitgeber hat
  const beschaeftigungsart = modelContext.dataStack[3].beschaeftigungsart;
  for (const artOhneArbeitgeber of BESCHAEFTIGUNGSARTEN_OHNE_ARBEITGEBER) {
    if (artOhneArbeitgeber === beschaeftigungsart) {
      return false;
    }
  }
  return true;
}

export function isBerufsInfoForPersonRequired(antrag: Antragdata, person: Person): boolean {
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);

  return fragen.berufsinfo;
}

export const isAdresseRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  return isAdresseForPersonRequired(antrag, person);
};

export function isAdresseForPersonRequired(antrag: Antragdata, person: Person): boolean {
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);
  return fragen.adresse;
}

export const isSozVersDatenRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  return isSozVersDatenForPersonRequired(antrag, person);
};

export function isSozVersDatenForPersonRequired(antrag: Antragdata, person: Person): boolean {
  if (
    (!person || !person.tarife || person.tarife.length === 0) &&
    (!person.abweichendeSozVersAnstalt ||
      (!!person.abweichendeSozVersAnstalt && person.abweichendeSozVersAnstalt.length === 0))
  ) {
    return false;
  }
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);
  return fragen.sozialVersDaten || (!!person.abweichendeSozVersAnstalt && person.abweichendeSozVersAnstalt.length > 0);
}

export const isNationalitaetRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const antrag: Antragdata = findAntragdataInContext(modelContext);
  const person: Person = findPersonInContext(modelContext);
  return isNationalitaetForPersonRequired(antrag, person);
};

export function isNationalitaetForPersonRequired(antrag: Antragdata, person: Person): boolean {
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);
  return fragen.nationalitaet;
}

export const berufsbezeichnungValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const simpleValidationResult: ISimpleValidationResult = isBerufsbezeichnungValid(modelContext.value);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: 'berufsbezeichnung',
      message: simpleValidationResult.message
    };
  }
};

export const beschaeftigungsArtValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const beschaeftigungsart: number = modelContext?.value;
  const partnerIndex: number = modelContext?.dataStack?.length - 1;
  const berufsbezeichnung: number = modelContext?.dataStack?.[partnerIndex]?.berufsbezeichnung;

  const simpleValidationResult: ISimpleValidationResult = isBeschaeftigungsartValid(
    beschaeftigungsart,
    berufsbezeichnung
  );

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: 'beschaeftigungsart',
      message: simpleValidationResult.message
    };
  }
};

const UNGUELTIGE_BESCHAEFTIGUNGSARTEN = {
  '0': 'unbekannte Beschäftigungsart',
  '1': 'keine Beschäftigungsart',
  '13': 'Sonstige'
};

export const BESCHAEFTIGUNGSART_SELBSTAENDIG = 2;

const BERUFE: Beruf[] = [
  { hostKey: 1, name: 'Land- und Forstwirtschaft, unselbstaendige Arbeiter', isNeverSelfEmployed: true },
  { hostKey: 10, name: 'ohne Beruf', isNeverSelfEmployed: true },
  { hostKey: 11, name: 'unbekannter Beruf' },
  { hostKey: 12, name: 'Hilfs-/Gelegenheitsarbeiter manuelle Tätigkeit' },
  { hostKey: 13, name: 'Hilfs-/Gelegenheitsarbeiter nicht manuelle Tätigkeit' },
  { hostKey: 14, name: 'Bergführer' },
  { hostKey: 15, name: 'Sport- und Skilehrer' },
  { hostKey: 16, name: 'Sportler beruflich' },
  { hostKey: 17, name: 'Chemiker, chem. Industrie' },
  { hostKey: 18, name: 'Presse, Rundfunk, Fernsehen' },
  { hostKey: 19, name: 'Vergnügungsgewerbe' },
  { hostKey: 2, name: 'Land- und Forstwirtschaft, Selbständige', isAlwaysSelfEmployed: true },
  { hostKey: 20, name: 'Land/Forstwirtschaft' },
  { hostKey: 3, name: 'Arbeiter in Bergbau, Industrie, Verkehr, Gewerbe', isNeverSelfEmployed: true },
  { hostKey: 30, name: 'Gewerbebedienstete ohne manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 31, name: 'Gewerbebedienstete manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 32, name: 'Baugewerbe u. verwandte Berufe' },
  { hostKey: 33, name: 'Gebäudereiniger' },
  { hostKey: 34, name: 'Gerüster' },
  { hostKey: 35, name: 'Bergbau/Steinbruch mit Sprengstoff' },
  { hostKey: 36, name: 'Bergbau/Steinbruch ohne Sprengstoff' },
  { hostKey: 37, name: 'Tänzer, Tanzlehrer' },
  { hostKey: 4, name: 'Hausfrauen und Hauswirtschaft' },
  { hostKey: 40, name: 'Haushalt (beschäftigt in)', isNeverSelfEmployed: true },
  { hostKey: 41, name: 'Hausfrau' },
  { hostKey: 42, name: 'Flugrisken beruflich' },
  { hostKey: 5, name: 'Angestellte Privat ohne Handel', isNeverSelfEmployed: true },
  { hostKey: 50, name: 'Privatbedienstete ohne manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 51, name: 'Privatbedienstete manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 52, name: 'Berufskraftfahrer' },
  { hostKey: 53, name: 'Krankenschwester, -pfleger' },
  { hostKey: 54, name: 'Tierhändler und -pfleger' },
  { hostKey: 55, name: 'Feuerwehr im Einsatzdienst', isNeverSelfEmployed: true },
  { hostKey: 56, name: 'Bundesheerangehörige (nicht in Verwaltung)', isNeverSelfEmployed: true },
  { hostKey: 57, name: 'Fluglotsen' },
  { hostKey: 58, name: 'Sicherheitsorgane Innendienst' },
  { hostKey: 59, name: 'Sicherheitsorgane Außendienst' },
  { hostKey: 6, name: 'Angestellte im Handel', isNeverSelfEmployed: true },
  { hostKey: 60, name: 'Handelsbedienstete ohne manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 61, name: 'Handelsbedienstete manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 7, name: 'Angestellte Öffentlich', isNeverSelfEmployed: true },
  { hostKey: 70, name: 'Beamte ohne manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 71, name: 'Vertragsbedienstete ohne manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 72, name: 'Lehrer' },
  { hostKey: 73, name: 'Richter/Staatsanwalt' },
  { hostKey: 74, name: 'Arzt' },
  { hostKey: 75, name: 'Tierarzt' },
  { hostKey: 76, name: 'Beamte  manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 77, name: 'Vertragsbedienstete  manuelle Tätigkeit', isNeverSelfEmployed: true },
  { hostKey: 78, name: 'Bahnpersonal (Fahr-,Strecken-,Verschubdienst)', isNeverSelfEmployed: true },
  { hostKey: 8, name: 'Selbständige, Kaufleute, Freischaffende, Unternehmer', isAlwaysSelfEmployed: true },
  { hostKey: 80, name: 'Unternehmer ohne manuelle Tätigkeit', isAlwaysSelfEmployed: true },
  { hostKey: 81, name: 'Kaufleute' },
  { hostKey: 82, name: 'Freischaffende ohne manuelle Tätigkeit', isAlwaysSelfEmployed: true },
  { hostKey: 83, name: 'Architekt' },
  { hostKey: 84, name: 'Notar' },
  { hostKey: 85, name: 'Rechtsanwalt' },
  { hostKey: 86, name: 'Gastwirt/Hotelier' },
  { hostKey: 87, name: 'Alkohol- und Gaststättengewerbe' },
  { hostKey: 88, name: 'Unternehmer manuelle Tätigkeit', isAlwaysSelfEmployed: true },
  { hostKey: 89, name: 'Freischaffende manuelle Tätigkeit', isAlwaysSelfEmployed: true },
  { hostKey: 9, name: 'Rentner, Pensionisten, Berufslose', isNeverSelfEmployed: true },
  { hostKey: 90, name: 'Pensionist', isNeverSelfEmployed: true }
];

export const BERUFE_KEYS_NICHT_SELBSTAENDIG: Set<number> = new Set(
  BERUFE.filter((beruf) => beruf?.isNeverSelfEmployed === true).map((beruf) => beruf?.hostKey)
);

export const ERROR_MESSAGE_BERUF_NICHT_SELBSTAENDIG = 'Berufsbezeichnung nicht selbständig';

export function isBerufsbezeichnungValid(hostKey: number): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };
  if (hostKey === 11) {
    result.valid = false;
    result.message = `Auswahl 'unbekannter Beruf' nicht erlaubt`;
  }
  return result;
}

export function isBeschaeftigungsartValid(
  beschaeftigungsart: number,
  berufsbezeichnung: number
): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };
  // Die strengeren Regeln für die UV VPs sollen generell gelten
  if (UNGUELTIGE_BESCHAEFTIGUNGSARTEN[beschaeftigungsart]) {
    result.valid = false;
    result.message = `Auswahl '` + UNGUELTIGE_BESCHAEFTIGUNGSARTEN[beschaeftigungsart] + `' nicht erlaubt`;
  }
  if (
    beschaeftigungsart === BESCHAEFTIGUNGSART_SELBSTAENDIG &&
    berufsbezeichnung &&
    BERUFE_KEYS_NICHT_SELBSTAENDIG.has(berufsbezeichnung)
  ) {
    result.valid = false;
    result.message = ERROR_MESSAGE_BERUF_NICHT_SELBSTAENDIG;
  }
  return result;
}

export const berufsbezeichnungVorbelegen: DefaultValueHandler = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): number => {
  return berufInfoVorbelegen(modelContext.dataStack[0].gebdatum, 10);
};

export const beschaeftigungsArtVorbelegen: DefaultValueHandler = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): number => {
  return berufInfoVorbelegen(modelContext.dataStack[0].gebdatum, 6);
};

export function berufInfoVorbelegen(gebdatum: string, value: number) {
  const alter = getAlterForGeburtstag(gebdatum);
  if (alter >= 6 && alter < 14) {
    return value;
  }
}

export const isArbeitgeberValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const arbeitgeber: string = modelContext.value || '';
  // Wenn ein Arbeitgeber eingegeben wurde, sind mindestens 2 sinnvolle Zeichen notwendig
  if (
    (arbeitgeber.length > 0 && arbeitgeber.replace(/[^A-Za-z0-9üöäÜÖÄß]/g, '').length >= 2) ||
    arbeitgeber.length === 0
  ) {
    return null;
  } else {
    return {
      id: 'beschaeftigtBei',
      message: 'Bitte geben Sie für den Arbeitgeber mindestens 2 Zeichen (Buchstaben, Zahlen) ein!'
    };
  }
};
